import { Check } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { money } from "@/studio/ui";
import { AddRoomBar, BudgetChoice, newRoom, recFor } from "@/studio/RoomsEditor";
import { num } from "@/studio/rooms";
import { Block, Chips, More, Q, Seg, Slider, Text } from "@/studio/intake/bits";

const GOALS = ["Improve appearance", "Create more space / storage", "Improve functionality", "Complete makeover", "Other"];
const STORAGE = ["Closet / wardrobe", "Media storage", "Display shelving", "Hidden storage", "Toy storage", "Bar / pantry"];
const WFH_TYPES = ["primary_bedroom", "secondary_bedroom", "living_room", "living_dining_flex", "other"];
const KIDS_TYPES = ["secondary_bedroom", "living_room", "dining_room", "living_dining_flex", "patio", "other"];
const DEAL = ["Glass tables", "White upholstery", "Tufted / ornate", "Heavy patterns", "Faux plants", "Oversized pieces", "Metallic finishes", "Other"];

export const StepRooms = ({ ctx }) => {
  const { rooms, setRooms, ra, setRa, meta } = ctx;
  return (
    <div>
      <p className="st-ask">Let's walk through each room — what it's for and what you'd love to change.</p>
      {rooms.map((r, i) => {
        const a = ra(r.id);
        const set = (patch) => setRa(r.id, patch);
        const kids = (a.kids_pets || []).some((k) => k !== "Neither");
        return (
          <Block key={r.id} title={r.label} meta={(a.goals || []).length ? `${a.goals.length} goals` : "Not started"} defaultOpen={i === 0} testId={`s1-room-${i}`}>
            <Q label="Purpose"><Text value={r.purpose} onChange={(v) => setRooms((rs) => rs.map((x) => (x.id === r.id ? { ...x, purpose: v } : x)))} placeholder="What is this room for?" testId={`s1-room-${i}-purpose`} /></Q>
            <Q label="What would you like to achieve?"><Chips options={GOALS} value={a.goals} onChange={(v) => set({ goals: v })} testId={`s1-room-${i}-goals`} /></Q>
            {(a.goals || []).includes("Other") && <div style={{ marginTop: 8 }}><Text value={a.goal_other} onChange={(v) => set({ goal_other: v })} placeholder="Describe the goal" testId={`s1-room-${i}-goal-other`} /></div>}
            {(a.goals || []).includes("Create more space / storage") && (
              <Q label="What needs a home?"><Chips options={STORAGE} value={a.storage} onChange={(v) => set({ storage: v })} testId={`s1-room-${i}-storage`} /></Q>
            )}
            <Q label="What isn't working?"><Text value={a.not_working} onChange={(v) => set({ not_working: v })} placeholder="e.g. Too dark, no place for shoes" testId={`s1-room-${i}-not-working`} /></Q>
            <Q label="Anything you must keep?"><Text value={a.must_keep} onChange={(v) => set({ must_keep: v })} placeholder="e.g. Grandmother's dresser" testId={`s1-room-${i}-must-keep`} /></Q>
            <More testId={`s1-room-${i}-more`}>
              {WFH_TYPES.includes(r.type) && <Q label="Work from home here?"><Chips single options={["No", "Occasionally", "Daily — needs a desk"]} value={a.wfh} onChange={(v) => set({ wfh: v })} testId={`s1-room-${i}-wfh`} /></Q>}
              {KIDS_TYPES.includes(r.type) && <Q label="Kids or pets using this room?"><Chips options={["Kids", "Pets", "Neither"]} value={a.kids_pets} onChange={(v) => set({ kids_pets: v.includes("Neither") && !(a.kids_pets || []).includes("Neither") ? ["Neither"] : v.filter((x) => x !== "Neither") })} testId={`s1-room-${i}-kids`} /></Q>}
              {kids && <Q label="Helpful for kids & pets"><Chips options={["Performance fabrics", "Rounded corners", "Easy-clean surfaces", "Secure storage"]} value={a.kids_needs} onChange={(v) => set({ kids_needs: v })} testId={`s1-room-${i}-kids-needs`} /></Q>}
            </More>
          </Block>
        );
      })}
      <div style={{ marginTop: 18 }}><p className="st-label">+ Add room</p><AddRoomBar meta={meta} where="intake" onAdd={(t) => setRooms((rs) => [...rs, newRoom(rs, t)])} /></div>
    </div>
  );
};

export const budgetTotal = (rooms, serverRooms, previews) => rooms.reduce(([lo, hi, miss], r) => {
  const rec = recFor(r, serverRooms, previews);
  if (r.budget_mode !== "custom" && rec) return [lo + rec[0], hi + rec[1], miss];
  if (num(r.budget_min) != null) return [lo + num(r.budget_min), hi + (num(r.budget_max) ?? num(r.budget_min)), miss];
  return [lo, hi, miss + 1];
}, [0, 0, 0]);

export const StepBudget = ({ ctx }) => {
  const { rooms, setRooms, pa, setPa, serverRooms, previews } = ctx;
  const [lo, hi, miss] = budgetTotal(rooms, serverRooms, previews);
  return (
    <div>
      <p className="st-ask">Here's what we usually recommend for furniture in each room. Does that feel right?</p>
      {rooms.map((r, i) => (
        <div key={r.id} className="st-toggle-row" style={{ alignItems: "flex-start", flexDirection: "column" }} data-testid={`s1-budget-room-${i}`}>
          <strong style={{ fontWeight: 600 }}>{r.label}</strong>
          <BudgetChoice room={r} rec={recFor(r, serverRooms, previews)} tid={`s1-budget-${i}`} update={(p) => setRooms((rs) => rs.map((x) => (x.id === r.id ? { ...x, ...p } : x)))} />
        </div>
      ))}
      <div className="st-panel st-panel--budget" style={{ marginTop: 18, padding: "18px 20px" }}>
        <div className="st-row" style={{ padding: 0 }}><span className="st-label">Combined furniture budget</span>
          <span className="st-price__total" data-testid="s1-budget-total">{lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`}</span></div>
        {miss > 0 && <p className="st-muted st-small">{miss} room(s) still need an amount.</p>}
        <p className="st-note">Furniture budgets exclude design fees, delivery, and assembly.</p>
      </div>
      <Q label="Design timeline"><Seg options={[["2", "2 weeks"], ["4", "4 weeks"]]} value={pa.timeline} onChange={(v) => setPa({ timeline: v })} testId="s1-timeline" /></Q>
      <Q label="Furniture delivery window" hint="Delivery estimates depend on vendor availability.">
        <Seg options={[["4", "4 weeks"], ["6", "6 weeks"], ["8", "8 weeks"], ["12", "12 weeks"]]} value={pa.delivery} onChange={(v) => setPa({ delivery: v })} testId="s1-delivery" />
      </Q>
    </div>
  );
};

const img = (u) => (u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const StylePicker = ({ library, sel, onChange, tid, small }) => {
  const toggleStyle = (id) => {
    const on = sel.styles.includes(id);
    if (!on && sel.styles.length >= 3) { toast("Up to 3 styles keeps the direction clear. Deselect one to swap."); return; }
    onChange({ ...sel, styles: on ? sel.styles.filter((s) => s !== id) : [...sel.styles, id] });
  };
  const togglePal = (id) => onChange({ ...sel, palettes: sel.palettes.includes(id) ? sel.palettes.filter((p) => p !== id) : [...sel.palettes, id] });
  return (
    <>
      <div className={`st-cards ${small ? "st-cards--sm" : ""}`} data-testid={`${tid}-styles`}>
        {library.styles.map((s) => {
          const on = sel.styles.includes(s.id);
          return (
            <button key={s.id} type="button" className={`st-card ${on ? "is-on" : ""}`} onClick={() => toggleStyle(s.id)} data-testid={`${tid}-style-${s.id}`}>
              <img src={img(s.image_url)} alt={s.name} loading="lazy" />
              <span>{s.name}</span>{on && <i className="st-card__check"><Check size={14} /></i>}
            </button>
          );
        })}
      </div>
      <p className="st-muted st-small" data-testid={`${tid}-style-count`}>{sel.styles.length} of 3 selected</p>
      <Q label={small ? "Palettes for this room" : "And the colors you're drawn to"}>
        <div className="st-cards" data-testid={`${tid}-palettes`}>
          {library.palettes.map((p) => (
            <button key={p.id} type="button" className={`st-pal ${sel.palettes.includes(p.id) ? "is-on" : ""}`} onClick={() => togglePal(p.id)} data-testid={`${tid}-palette-${p.id}`}>
              <div className="st-swatches">{p.swatches.map((c) => <i key={c} style={{ background: c }} />)}</div>{p.name}
            </button>
          ))}
        </div>
      </Q>
    </>
  );
};

export const StepStyle = ({ ctx }) => {
  const { library, selections, setSelections, rooms, ra, setRa } = ctx;
  const roomSel = (rid) => selections.rooms?.[rid] || { styles: [], palettes: [] };
  return (
    <div>
      <p className="st-ask">Which of these feel like you? Pick up to three.</p>
      <StylePicker library={library} sel={selections} onChange={setSelections} tid="s1" />
      <Q label="Room-specific style" hint="The project style applies to every room unless a room needs its own.">
        {rooms.map((r, i) => (
          <div key={r.id} className="st-toggle-row" style={{ flexDirection: "column", alignItems: "stretch" }} data-testid={`s1-room-style-${i}`}>
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span>{r.label} <span className="st-muted st-small">· Different style for this room</span></span>
              <Switch checked={!!ra(r.id).own_style} onCheckedChange={(v) => setRa(r.id, { own_style: v })} data-testid={`s1-room-style-${i}-toggle`} />
            </label>
            {ra(r.id).own_style && <div style={{ marginTop: 12 }}><StylePicker small library={library} sel={roomSel(r.id)} tid={`s1-room-${i}`}
              onChange={(v) => setSelections({ ...selections, rooms: { ...(selections.rooms || {}), [r.id]: v } })} /></div>}
          </div>
        ))}
      </Q>
    </div>
  );
};

export const StepPriorities = ({ ctx }) => {
  const { pa, setPa } = ctx;
  return (
    <div>
      <p className="st-ask">When we have to choose, what matters more?</p>
      <Q label="Budget vs style"><Slider left="Staying within budget" right="Maximizing style" value={pa.budget_style} onChange={(v) => setPa({ budget_style: v })} testId="s1-slider-budget-style" /></Q>
      <Q label="Speed vs cost"><Slider left="Faster delivery" right="Lower cost" value={pa.speed_cost} onChange={(v) => setPa({ speed_cost: v })} testId="s1-slider-speed-cost" /></Q>
      <Q label="Anything you'd rather not see?"><Chips options={DEAL} value={pa.deal_breakers} onChange={(v) => setPa({ deal_breakers: v })} testId="s1-deal" /></Q>
      {(pa.deal_breakers || []).includes("Other") && <div style={{ marginTop: 8 }}><Text value={pa.deal_other} onChange={(v) => setPa({ deal_other: v })} placeholder="Other deal breakers" testId="s1-deal-other" /></div>}
      <More label="Notes" testId="s1-notes-more"><Q label="Notes"><Text area value={pa.notes} onChange={(v) => setPa({ notes: v })} placeholder="Anything else from the conversation" testId="s1-notes" /></Q></More>
    </div>
  );
};
